import i18n from 'i18next'
import { useSyncExternalStore } from 'react'
import { initReactI18next } from 'react-i18next'
import auth from './locales/ru/auth.json'
import carts from './locales/ru/carts.json'
import catalog from './locales/ru/catalog.json'
import clients from './locales/ru/clients.json'
import common from './locales/ru/common.json'
import exchange from './locales/ru/exchange.json'
import home from './locales/ru/home.json'
import news from './locales/ru/news.json'
import notifications from './locales/ru/notifications.json'
import order from './locales/ru/order.json'
import orderCatalog from './locales/ru/order-catalog.json'
import pcBuild from './locales/ru/pc-build.json'
import prices from './locales/ru/prices.json'
import processes from './locales/ru/processes.json'
import registration from './locales/ru/registration.json'
import reports from './locales/ru/reports.json'
import search from './locales/ru/search.json'
import serials from './locales/ru/serials.json'
import validation from './locales/ru/validation.json'

/** Языки интерфейса. Порядок массива — порядок пунктов в переключателе. */
export const SUPPORTED_LOCALES = ['ru', 'en', 'zh'] as const

export type Locale = (typeof SUPPORTED_LOCALES)[number]

/** Основной и запасной язык: лежит в бандле, из него выводятся типы ключей (`i18next.d.ts`). */
export const DEFAULT_LOCALE: Locale = 'ru'

/** Язык хранится в браузере, как и тема: это настройка устройства, а не сеанса. */
const LOCALE_STORAGE_KEY = 'locale'

/**
 * Значение `lang` для языка: корень документа и подписи с самоназваниями. Китайскому нужен
 * явный вариант письма: по голому `zh` браузер вправе взять японское начертание общих иероглифов.
 */
const HTML_LANG: Record<Locale, string> = { ru: 'ru', en: 'en', zh: 'zh-CN' }

export function htmlLangOf(locale: Locale): string {
  return HTML_LANG[locale]
}

/**
 * Остальные языки — отдельными чанками, по файлу на неймспейс: грузятся, только когда выбраны.
 * Каждый весит ~10 КБ gzip, и возить их в бандле первого экрана тем, кто язык не меняет, незачем.
 * Состав словарей сверяет с `ru` тест `locales.test.ts`.
 */
const LAZY_BUNDLES = import.meta.glob<Record<string, unknown>>(
  ['./locales/en/*.json', './locales/zh/*.json'],
  { import: 'default' },
)

function isLocale(value: string | null | undefined): value is Locale {
  return SUPPORTED_LOCALES.some((locale) => locale === value)
}

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
    return isLocale(stored) ? stored : DEFAULT_LOCALE
  } catch {
    return DEFAULT_LOCALE
  }
}

// Подписка до init: i18next объявляет язык событием уже на старте, и корень получает lang до рендера
i18n.on('languageChanged', (language) => {
  if (typeof document === 'undefined') return
  document.documentElement.lang = htmlLangOf(isLocale(language) ? language : DEFAULT_LOCALE)
})

void i18n.use(initReactI18next).init({
  resources: {
    ru: {
      common,
      validation,
      home,
      auth,
      registration,
      order,
      'order-catalog': orderCatalog,
      'pc-build': pcBuild,
      processes,
      catalog,
      clients,
      carts,
      news,
      notifications,
      search,
      serials,
      prices,
      reports,
      exchange,
    },
  },
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  defaultNS: 'common',
  ns: [
    'common',
    'validation',
    'home',
    'auth',
    'registration',
    'order',
    'order-catalog',
    'pc-build',
    'processes',
    'catalog',
    'clients',
    'carts',
    'news',
    'notifications',
    'search',
    'serials',
    'prices',
    'reports',
    'exchange',
  ],
  interpolation: { escapeValue: false },
})

/** Языки, чьи словари уже в памяти. Русский лежит в бандле. */
const loaded = new Set<Locale>([DEFAULT_LOCALE])

/** Загрузки в полёте: повторный выбор того же языка ждёт ту же загрузку, а не заводит вторую. */
const loading = new Map<Locale, Promise<void>>()

function loadLocale(locale: Locale): Promise<void> {
  if (loaded.has(locale)) return Promise.resolve()

  const known = loading.get(locale)
  if (known !== undefined) return known

  const prefix = `./locales/${locale}/`
  const pending = Promise.all(
    Object.entries(LAZY_BUNDLES)
      .filter(([path]) => path.startsWith(prefix))
      .map(async ([path, load]) => {
        i18n.addResourceBundle(locale, path.slice(prefix.length, -'.json'.length), await load())
      }),
  ).then(() => {
    loaded.add(locale)
  })

  loading.set(locale, pending)
  // Удачная загрузка остаётся в `loaded`, сбой забывается: следующий выбор пробует снова
  void pending.then(
    () => loading.delete(locale),
    () => loading.delete(locale),
  )
  return pending
}

/**
 * Язык, чей словарь сейчас грузится, — для ожидания на кнопках выбора. `null` — ничего
 * не грузится: загруженный язык включается мгновенно, и спиннер на нём только мигнул бы.
 */
let pendingLocale: Locale | null = null
const pendingListeners = new Set<() => void>()

function setPendingLocale(next: Locale | null): void {
  if (pendingLocale === next) return
  pendingLocale = next
  for (const listener of pendingListeners) listener()
}

function subscribePendingLocale(listener: () => void): () => void {
  pendingListeners.add(listener)
  return () => {
    pendingListeners.delete(listener)
  }
}

const readPendingLocale = () => pendingLocale

/** Язык, чей словарь едет по сети прямо сейчас; `null` — ничего не грузится. */
export function usePendingLocale(): Locale | null {
  return useSyncExternalStore(subscribePendingLocale, readPendingLocale, readPendingLocale)
}

/** Последний запрошенный язык: пока грузился словарь, пользователь мог выбрать другой. */
let requested: Locale = DEFAULT_LOCALE

/** Текущий язык интерфейса: `Accept-Language`, форматы `Intl` и отметка в переключателе. */
export function getLocale(): Locale {
  return isLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE
}

/**
 * Смена языка на лету: словарь подгружается при первом выборе, подписи перерисовываются без
 * перезагрузки, выбор запоминается. Отказ — словарь последнего выбора не загрузился, язык
 * остаётся прежним. Сбой устаревшего выбора не всплывает: действует последний.
 * Кеш запросов не сбрасывается — тексты интерфейса фронт берёт из своих ключей по коду,
 * а не из ответа сервера (скилл i18n, «Ошибки от сервера»).
 */
export async function setLocale(locale: Locale): Promise<void> {
  requested = locale
  setPendingLocale(loaded.has(locale) ? null : locale)

  try {
    await loadLocale(locale)
  } catch (error) {
    if (requested !== locale) return
    setPendingLocale(null)
    throw error
  }
  if (requested !== locale) return

  setPendingLocale(null)
  await i18n.changeLanguage(locale)
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    /* приватный режим — язык не переживёт перезагрузку */
  }
}

/** Язык прошлого визита. `main.tsx` ждёт его до первого рендера — иначе мигнул бы русский. */
export async function restoreLocale(): Promise<void> {
  const stored = readStoredLocale()
  if (stored === DEFAULT_LOCALE) return

  try {
    await setLocale(stored)
  } catch {
    /* словарь не загрузился — остаёмся на русском, язык можно выбрать заново в шапке */
  }
}

export default i18n
